import type { ActionRecord, EvidenceBlob } from "@sequesign/lib/types";

interface WarningsProps {
  actions: ActionRecord[];
  evidence: Map<string, EvidenceBlob>;
}

interface DerivedWarning {
  severity: "info" | "warning" | "critical";
  code: string;
  message: string;
  actionSequence?: number;
  actionType?: string;
}

interface ClaimEntry {
  claim?: unknown;
  external_attestation?: unknown;
}

function unsupportedClaimsFor(blob: EvidenceBlob): string[] {
  const content = blob.content as Record<string, unknown> | null;
  if (!content || typeof content !== "object") return [];
  const raw = (content as { unsupported_claims?: unknown }).unsupported_claims;
  if (!Array.isArray(raw)) return [];
  const claims: string[] = [];
  for (const entry of raw as ClaimEntry[]) {
    if (
      entry &&
      typeof entry === "object" &&
      typeof entry.claim === "string" &&
      entry.external_attestation === null
    ) {
      claims.push(entry.claim);
    }
  }
  return claims;
}

export function Warnings({ actions, evidence }: WarningsProps) {
  const warnings: DerivedWarning[] = [];

  for (const action of actions) {
    const blob = evidence.get(action.action_id);
    if (!blob) continue;
    for (const claim of unsupportedClaimsFor(blob)) {
      warnings.push({
        severity: "warning",
        code: "unsupported_agent_claim",
        message: `Agent claim "${claim}" lacks external attestation. The receipt is cryptographically valid; the claim itself is only agent-asserted.`,
        actionSequence: action.sequence,
        actionType: action.action_type
      });
    }
  }

  if (warnings.length === 0) return null;

  return (
    <div className="section">
      <h3 className="section-title">Warnings</h3>
      <div className="warnings">
        {warnings.map((w, i) => (
          <div className={`warning ${w.severity}`} key={i}>
            <div className="warning-head">
              <span className="warning-severity">{w.severity}</span>
              <span className="warning-code">{w.code}</span>
            </div>
            <p className="warning-message">{w.message}</p>
            {typeof w.actionSequence === "number" ? (
              <p className="warning-target">
                Affected action: sequence {w.actionSequence}
                {w.actionType ? ` (${w.actionType})` : ""}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
